import React, { useState, useMemo, useEffect } from "react";
import { useSelector } from "react-redux";
import {
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Copy,
  Search,
} from "lucide-react";
import Citiescommondropdown from "../../common/Citiescommondropdown";
import toast from "react-hot-toast";
import formatDateForApi from "../../common/formatDateForApi";
import DaterangePicker from "../../common/Daterangepicker";
import DashboardSkeleton from "../../common/DashboardSkeleton";

// --- DUMMY DATA ---
const DUMMY_SUMMARY = {
  damage: {
    totalLoss: "₹ 0",
    count: 0,
    charged: "₹ 0",
    recovered: "₹ 0",
    feCollected: "₹ 0",
    unrecovered: "₹ 0",
  },
  downtime: {
    totalLoss: "₹ 0",
    count: 0,
    charged: "₹ 0",
    recovered: "₹ 0",
    feCollected: "₹ 0",
    unrecovered: "₹ 0",
  },
};

const getStatusBadge = (status) => {
  switch (status?.toLowerCase()) {
    case "cleared":
      return "bg-green-100 text-green-700";
    case "partial":
      return "bg-amber-100 text-amber-700";
    case "pending":
    default:
      return "bg-yellow-100 text-orange-800";
  }
};

const PAGE_SIZE = 5;

const TablePagination = ({ page, totalItems, onPageChange }) => {
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);

  if (totalPages <= 1) return null;

  return (
    <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100 text-xs text-slate-500">
      <span>
        Page {page} of {totalPages}
      </span>
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onPageChange(page - 1)}
          disabled={page === 1}
          className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
          aria-label="Previous page"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => onPageChange(page + 1)}
          disabled={page === totalPages}
          className="p-1.5 rounded border border-slate-200 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-50"
          aria-label="Next page"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const Damagedowntimedashboard = () => {
  const cities = useSelector((state) => state?.cities?.citiesData);

  const apiUrl = process.env.REACT_APP_API_BASE_URL;
  const token = localStorage.getItem("token");

  // Default to Bangalore (ID: "1")
  const [loading, setLoading] = useState(false);
  const [cityFilter, setCityFilter] = useState("");
  const [summaryData, setSummaryData] = useState(null);
  const today = new Date();
  today.setHours(23, 59, 59, 999);
  const oneMonthAgo = new Date();
  oneMonthAgo.setMonth(today.getMonth() - 1);
  const [selectionRange, setSelectionRange] = useState({
    startDate: oneMonthAgo,
    endDate: today,
    key: "selection",
  });

  // Individual Search Filters per Table
  const [damageVehicleSearch, setDamageVehicleSearch] = useState("");
  const [damageEmployeeSearch, setDamageEmployeeSearch] = useState("");
  const [downtimeVehicleSearch, setDowntimeVehicleSearch] = useState("");
  const [downtimeEmployeeSearch, setDowntimeEmployeeSearch] = useState("");
  const [damageVehiclePage, setDamageVehiclePage] = useState(1);
  const [damageEmployeePage, setDamageEmployeePage] = useState(1);
  const [downtimeVehiclePage, setDowntimeVehiclePage] = useState(1);
  const [downtimeEmployeePage, setDowntimeEmployeePage] = useState(1);

  // Only real available cities - "All Cities" removed
  const cityOptions = useMemo(() => {
    if (!cities) return [];
    return Object.entries(cities).map(([key, value]) => ({
      value: key,
      label: value,
    }));
  }, [cities]);

  // Copy License Plate Handler
  const handleCopyPlate = (plate) => {
    navigator.clipboard.writeText(plate);
    toast.success(`Copied: ${plate}`, {
      position: "top-center",
      style: {
        background: "white",
        color: "#111827",
        fontWeight: "550",
        border: "1px solid #E5E7EB",
      },
    });
  };

  // Filtered lists for each table
  const filteredDamageVehicles = useMemo(() => {
    return (summaryData?.vehicleDamageList || []).filter((row) =>
      row.licensePlate
        ?.toLowerCase()
        .includes(damageVehicleSearch.trim().toLowerCase()),
    );
  }, [summaryData, damageVehicleSearch]);

  const filteredDamageEmployees = useMemo(() => {
    return (summaryData?.employeeDamageList || []).filter((emp) =>
      emp.name
        .toLowerCase()
        .includes(damageEmployeeSearch.trim().toLowerCase()),
    );
  }, [summaryData, damageEmployeeSearch]);

  const filteredDowntimeVehicles = useMemo(() => {
    return (summaryData?.vehicleDowntimeList || []).filter((row) =>
      row.licensePlate
        ?.toLowerCase()
        .includes(downtimeVehicleSearch.trim().toLowerCase()),
    );
  }, [summaryData, downtimeVehicleSearch]);

  const filteredDowntimeEmployees = useMemo(() => {
    return (summaryData?.employeeDowntimeList || []).filter((emp) =>
      emp.name
        .toLowerCase()
        .includes(downtimeEmployeeSearch.trim().toLowerCase()),
    );
  }, [summaryData, downtimeEmployeeSearch]);

  const paginatedDamageVehicles = filteredDamageVehicles.slice(
    (damageVehiclePage - 1) * PAGE_SIZE,
    damageVehiclePage * PAGE_SIZE,
  );
  const paginatedDamageEmployees = filteredDamageEmployees.slice(
    (damageEmployeePage - 1) * PAGE_SIZE,
    damageEmployeePage * PAGE_SIZE,
  );
  const paginatedDowntimeVehicles = filteredDowntimeVehicles.slice(
    (downtimeVehiclePage - 1) * PAGE_SIZE,
    downtimeVehiclePage * PAGE_SIZE,
  );
  const paginatedDowntimeEmployees = filteredDowntimeEmployees.slice(
    (downtimeEmployeePage - 1) * PAGE_SIZE,
    downtimeEmployeePage * PAGE_SIZE,
  );

  useEffect(() => {
    setDamageVehiclePage(1);
  }, [damageVehicleSearch, summaryData]);

  useEffect(() => {
    setDamageEmployeePage(1);
  }, [damageEmployeeSearch, summaryData]);

  useEffect(() => {
    setDowntimeVehiclePage(1);
  }, [downtimeVehicleSearch, summaryData]);

  useEffect(() => {
    setDowntimeEmployeePage(1);
  }, [downtimeEmployeeSearch, summaryData]);

  useEffect(() => {
    getDeliveryandPickupsDataAPI();
  }, []);

  const getDeliveryandPickupsDataAPI = async () => {
    if (!token) {
      return toast.error("Session Expired Close all the tab and signin again");
    }
    const formattedStartDate = formatDateForApi(
      selectionRange?.startDate,
      "start",
    );
    const formattedEndDate = formatDateForApi(selectionRange?.endDate, "end");
    setLoading(true);
    let query = "";
    if (cityFilter && cityFilter !== "All Cities") {
      query += `&cityId=${cityFilter}`;
    }
    const url = `${apiUrl}/api/dashboardreports/damage-downtime-dashboard?startDate=${formattedStartDate}&endDate=${formattedEndDate}${query}`;
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `${token}`,
        },
      });
      const data = await response.json();
      if (data?.statusCode === "0100") {
        setLoading(false);
        setSummaryData(data?.dataValues);
      } else {
        setLoading(false);
        toast.error(data?.msg);
      }
      return data;
    } catch (error) {
      return;
      setLoading(false);
      console.error("Error fetching Hub Names:", error);
    }
  };

  return (
    <>
    {loading ? <><DashboardSkeleton/></> : <><div className="w-full flex flex-col gap-6 py-4 my-2">
      {/* Top Header Card */}
      <div className="w-full h-20 px-6 bg-white rounded-2xl shadow-sm outline outline-1 outline-slate-200 flex justify-between items-center">
        <h1 className="text-slate-900 text-xl md:text-2xl font-semibold tracking-tight uppercase">
          Damage &amp; Downtime Charges
        </h1>

        <div className="flex items-center gap-3">
          <DaterangePicker
            selectionRange={selectionRange}
            setSelectionRange={setSelectionRange}
            style={-10}
          />
          <div className="min-w-[190px] px-3 py-1.5 bg-white rounded-[10px] shadow-[0px_1px_2px_-1px_rgba(0,0,0,0.10)] outline outline-[0.80px] outline-offset-[-0.80px] outline-gray-200 inline-flex justify-start items-center gap-2">
            <Citiescommondropdown
              value={cityFilter}
              onChange={setCityFilter}
              options={[{ value: null, label: "All Cities" }, ...cityOptions]}
              placeholder="Select City"
              className="flex-1 min-w-[120px]"
              showSearch={true}
            />
          </div>
          <button
            onClick={() => {
              getDeliveryandPickupsDataAPI();
            }}
            className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-all duration-300 shadow-md hover:shadow-lg"
          >
            <Search size={18} />
            Search
          </button>
        </div>
      </div>

      <div className="w-full p-4 bg-white rounded-2xl outline outline-1 outline-neutral-200 shadow-sm flex flex-col gap-4">
        {/* <div className="w-full px-2 flex justify-between items-center">
          <span className="text-slate-900 text-xl font-semibold uppercase tracking-tight">
            Damage
          </span>
          <div className="flex items-center gap-4">
            <span className="text-slate-500 text-xs font-semibold uppercase">
              Loss For Company
            </span>
            <span className="text-slate-900 text-3xl xl:text-[40px] font-bold">
              {0}
            </span>
          </div>
        </div> */}

        <div className="w-full grid grid-cols-5 gap-2">
          {/* <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-amber-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Total Damage Count
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {0}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Across Fleet
            </div>
          </div> */}

          <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-red-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Total Damage Charged
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {"₹ " +summaryData?.totalDamageCharges}
              </div>
            </div>
            {/* <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Across Fleet
            </div> */}
          </div>

          <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-orange-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Total Downtime Charged
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {"₹ " +summaryData?.totalDowntimeCharges}
              </div>
            </div>
            {/* <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Total Fleet Downtime
            </div> */}
          </div>

          {/* <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-green-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Recovered
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {0}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Customer Collection
            </div>
          </div> */}

          {/* <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-blue-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              F.E Collected
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {0}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate opacity-0 select-none">
              -
            </div>
          </div> */}

          {/* <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-red-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Unrecovered
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {0}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Pending Triage
            </div>
          </div> */}
        </div>
      </div>

      <div className="w-full p-4 bg-white rounded-2xl outline outline-1 outline-neutral-200 shadow-sm flex flex-col gap-4">
        {/* <div className="w-full px-2 flex justify-between items-center">
          <span className="text-slate-900 text-xl font-semibold uppercase tracking-tight">
            Down Time
          </span>
          <div className="flex items-center gap-4">
            <span className="text-slate-500 text-xs font-semibold uppercase">
              Loss For Company
            </span>
            <span className="text-slate-900 text-3xl xl:text-[40px] font-bold">
              {0}
            </span>
          </div>
        </div> */}

        <div className="w-full grid grid-cols-5 gap-2">
          {/* <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-amber-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Total Downtime Count
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {0}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Across Fleet
            </div>
          </div> */}

          {/* <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-amber-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Total Downtime Charged
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {summaryData?.totalDowntimeCharges}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Total Fleet Downtime
            </div>
          </div> */}
{/* 
          <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-green-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Downtime Recovered
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {0}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Recovered Amount
            </div>
          </div> */}

          {/* <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-blue-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              F.E Collected
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {0}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate opacity-0 select-none">
              -
            </div>
          </div> */}

          {/* <div className="min-w-0 aspect-[215/135] p-3 xl:p-5 relative bg-white rounded-bl-xl rounded-br-xl shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)] outline outline-1 outline-offset-[-1px] outline-slate-200 flex flex-col justify-between items-start overflow-hidden">
            <div className="w-[90%] h-1.5 xl:h-2 left-[5%] top-0 absolute bg-red-600 rounded-bl-lg rounded-br-lg" />
            <span className="self-stretch text-slate-500 text-[10px] xl:text-xs font-semibold font-['Inter'] uppercase truncate leading-none">
              Downtime Unrecovered
            </span>
            <div className="self-stretch flex-1 flex flex-col justify-center items-start my-auto">
              <div className="self-stretch text-slate-900 text-xl xl:text-[42px] font-bold font-['Inter'] leading-none">
                {0}
              </div>
            </div>
            <div className="self-stretch text-slate-400 text-xs xl:text-sm font-normal font-['Inter'] leading-none truncate">
              Unrecovered Losses
            </div>
          </div> */}
        </div>
      </div>

      <div className="self-stretch p-4 bg-amber-100 rounded-xl outline outline-1 outline-offset-[-1px] outline-amber-200 inline-flex justify-start items-start gap-3">
        <AlertCircle className="size-5 text-amber-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1 justify-start">
          <span className="text-amber-800 text-sm font-bold font-['Inter'] leading-5">
            Note: <br />
          </span>
          <span className="text-amber-800 text-sm font-normal font-['Inter'] leading-5">
            <li>Damage recovery is tracked vehicle-wise and employee-wise.</li>
            <li>
              Employee responsible for damage gets salary deduction if proven.
            </li>
            <li>Customer damage recovery tracked separately per booking.</li>
          </span>
        </div>
      </div>

      <div className="w-full flex flex-col lg:flex-row gap-6 items-start">
        <div className="flex-1 w-full bg-white rounded-xl outline outline-1 outline-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap justify-between items-center gap-2">
            <h2 className="text-slate-900 text-sm font-bold uppercase">
              Damage - Vehicle Wise
            </h2>
            <div className="relative flex items-center">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search vehicle..."
                value={damageVehicleSearch}
                onChange={(e) => setDamageVehicleSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-stone-50 border-b border-slate-200 text-slate-400 text-xs font-semibold uppercase">
                  <th className="px-4 py-3">Vehicle</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 text-center">Cases</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">City</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {paginatedDamageVehicles.length > 0 ? (
                  paginatedDamageVehicles.map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                          <span>{row.licensePlate}</span>
                          <button
                            type="button"
                            onClick={() => handleCopyPlate(row.licensePlate)}
                            title="Copy Vehicle Plate"
                            className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-normal">
                        {row.name}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-900 font-normal">
                        {row.bookingCount}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {"₹ " + row.totalDamageCharges}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 font-normal">
                        {row.cityText}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No vehicles found matching "{damageVehicleSearch}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={damageVehiclePage}
            totalItems={filteredDamageVehicles.length}
            onPageChange={setDamageVehiclePage}
          />
        </div>

        <div className="flex-1 w-full bg-white rounded-xl outline outline-1 outline-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap justify-between items-center gap-2">
            <h2 className="text-slate-900 text-sm font-bold uppercase">
              Downtime - Vehicle Wise
            </h2>
            <div className="relative flex items-center">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search vehicle..."
                value={downtimeVehicleSearch}
                onChange={(e) => setDowntimeVehicleSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-stone-50 border-b border-slate-200 text-slate-400 text-xs font-semibold uppercase">
                  <th className="px-4 py-3">Vehicle</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 text-center">Cases</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">City</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {paginatedDowntimeVehicles.length > 0 ? (
                  paginatedDowntimeVehicles.map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                          <span>{row.licensePlate}</span>
                          <button
                            type="button"
                            onClick={() => handleCopyPlate(row.licensePlate)}
                            title="Copy Vehicle Plate"
                            className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-normal">
                        {row.name}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-900 font-normal">
                        {row.bookingCount}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {"₹ " + row.totalDowntimeCharges}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 font-normal">
                        {row.cityText}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No vehicles found matching "{downtimeVehicleSearch}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={downtimeVehiclePage}
            totalItems={filteredDowntimeVehicles?.length}
            onPageChange={setDowntimeVehiclePage}
          />
        </div>

        {/* <div className="w-full lg:w-[480px] bg-white rounded-xl outline outline-1 outline-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap justify-between items-center gap-2">
            <h2 className="text-slate-900 text-sm font-bold uppercase">
              Damage - Employee Wise
            </h2>
            <div className="relative flex items-center">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search employee..."
                value={damageEmployeeSearch}
                onChange={(e) => setDamageEmployeeSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-stone-50 border-b border-slate-200 text-slate-400 text-xs font-semibold uppercase">
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3 text-center">Incidents</th>
                  <th className="px-4 py-3 text-right">Charged</th>
                  <th className="px-4 py-3 text-right">Deducted</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {filteredDamageEmployees.length > 0 ? (
                  paginatedDamageEmployees.map((emp, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {emp.name}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-900">
                        {emp.incidents}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {emp.charged}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 font-normal">
                        {emp.deducted}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getStatusBadge(emp.status)}`}
                        >
                          {emp.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No employees found matching "{damageEmployeeSearch}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={damageEmployeePage}
            totalItems={filteredDamageEmployees.length}
            onPageChange={setDamageEmployeePage}
          />
        </div> */}
      </div>

      <div className="w-full flex flex-col lg:flex-row gap-6 items-start">
        {/* <div className="flex-1 w-full bg-white rounded-xl outline outline-1 outline-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap justify-between items-center gap-2">
            <h2 className="text-slate-900 text-sm font-bold uppercase">
              Downtime - Vehicle Wise
            </h2>
            <div className="relative flex items-center">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search vehicle..."
                value={downtimeVehicleSearch}
                onChange={(e) => setDowntimeVehicleSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-stone-50 border-b border-slate-200 text-slate-400 text-xs font-semibold uppercase">
                  <th className="px-4 py-3">Vehicle</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3 text-center">Cases</th>
                  <th className="px-4 py-3 text-right">Total</th>
                  <th className="px-4 py-3 text-right">City</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {paginatedDowntimeVehicles.length > 0 ? (
                  paginatedDowntimeVehicles.map((row, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="inline-flex items-center gap-1.5 font-semibold text-slate-900">
                          <span>{row.licensePlate}</span>
                          <button
                            type="button"
                            onClick={() => handleCopyPlate(row.licensePlate)}
                            title="Copy Vehicle Plate"
                            className="p-1 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-200 transition-colors"
                          >
                            <Copy className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-600 font-normal">
                        {row.name}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-900 font-normal">
                        {row.bookingCount}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {"₹ " + row.totalDowntimeCharges}
                      </td>
                      <td className="px-4 py-3 text-right text-green-600 font-normal">
                        {row.cityText}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No vehicles found matching "{downtimeVehicleSearch}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={downtimeVehiclePage}
            totalItems={filteredDowntimeVehicles?.length}
            onPageChange={setDowntimeVehiclePage}
          />
        </div> */}

        {/* <div className="w-full lg:w-[480px] bg-white rounded-xl outline outline-1 outline-slate-200 shadow-sm overflow-hidden">
          <div className="p-4 border-b border-slate-100 flex flex-wrap justify-between items-center gap-2">
            <h2 className="text-slate-900 text-sm font-bold uppercase">
              Downtime - Employee Wise
            </h2>
            <div className="relative flex items-center">
              <Search className="w-4 h-4 text-slate-400 absolute left-2.5 pointer-events-none" />
              <input
                type="text"
                placeholder="Search employee..."
                value={downtimeEmployeeSearch}
                onChange={(e) => setDowntimeEmployeeSearch(e.target.value)}
                className="pl-8 pr-3 py-1.5 text-xs bg-slate-50 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:bg-white transition-colors"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-stone-50 border-b border-slate-200 text-slate-400 text-xs font-semibold uppercase">
                  <th className="px-4 py-3">Employee</th>
                  <th className="px-4 py-3 text-center">Incidents</th>
                  <th className="px-4 py-3 text-right">Charged</th>
                  <th className="px-4 py-3 text-right">Deducted</th>
                  <th className="px-4 py-3 text-center">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-xs font-medium">
                {filteredDowntimeEmployees.length > 0 ? (
                  paginatedDowntimeEmployees.map((emp, idx) => (
                    <tr
                      key={idx}
                      className="hover:bg-slate-50 transition-colors"
                    >
                      <td className="px-4 py-3 font-semibold text-slate-900">
                        {emp.name}
                      </td>
                      <td className="px-4 py-3 text-center text-slate-900">
                        {emp.incidents}
                      </td>
                      <td className="px-4 py-3 text-right font-semibold text-slate-900">
                        {emp.charged}
                      </td>
                      <td className="px-4 py-3 text-right text-slate-600 font-normal">
                        {emp.deducted}
                      </td>
                      <td className="px-4 py-3 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${getStatusBadge(emp.status)}`}
                        >
                          {emp.status}
                        </span>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={5}
                      className="px-4 py-6 text-center text-slate-400"
                    >
                      No employees found matching "{downtimeEmployeeSearch}"
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <TablePagination
            page={downtimeEmployeePage}
            totalItems={filteredDowntimeEmployees.length}
            onPageChange={setDowntimeEmployeePage}
          />
        </div> */}
      </div>
    </div></>}
    </>
  );
};

export default Damagedowntimedashboard;
